import { useState, useMemo, useEffect } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useDebounce } from "@/hooks/use-debounce";
import { useInView } from "react-intersection-observer";
import { getOpportunities } from "@/lib/queries/catalog";
import { Loader2, X, ChevronsUpDown, Check, Filter, Target, Users, Globe } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type Opportunity = {
  id: string;
  name: string;
  community_need: string;
  volunteer_support: string;
  impact: string;
  beneficiary_types: string[];
  relevant_un_day: string[];
  focus_area: string;
  location?: string;
  format?: string;
  outcome?: string;
  output?: string;
  type: string[];
  status: string;
  is_active: boolean;
  created_by: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
};

interface SelectOpportunityProps {
  selectedOpportunity: Opportunity | null;
  onSelect: (opportunity: Opportunity) => void;
  onNext: (option?: string) => void;
  session: any;
  profile: any;
}

type EnumData = {
  data: {
    sdg: Array<{
      id: string;
      value: string;
    }>;
    opportunityType: string[];
    opportunityBeneficiaryType: string[];
  };
};

export function SelectOpportunity({
  selectedOpportunity,
  onSelect,
  onNext,
  session,
  profile
}: SelectOpportunityProps) {
  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDebounce(searchInput, 500);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Filter states
  const [openBeneficiary, setOpenBeneficiary] = useState(false);
  const [openSDG, setOpenSDG] = useState(false);
  const [openFocusArea, setOpenFocusArea] = useState(false);
  
  // Applied filter states
  const [appliedBeneficiaries, setAppliedBeneficiaries] = useState<string[]>([]);
  const [appliedSDGs, setAppliedSDGs] = useState<string[]>([]);
  const [appliedFocusAreas, setAppliedFocusAreas] = useState<string[]>([]);
  const LIMIT = 10;

  // Initialize filters from profile
  useEffect(() => {
    if (profile) {
      if (Array.isArray(profile.beneficiary_types)) {
        setAppliedBeneficiaries(profile.beneficiary_types);
      }

      if (Array.isArray(profile.sdgs)) {
        setAppliedSDGs(profile.sdgs);
      }

      if (Array.isArray(profile.cause_areas)) {
        setAppliedFocusAreas(profile.cause_areas);
      }
    }
  }, [profile]);

  // Fetch enums from API
  const { data: enumData, isLoading: isEnumsLoading } = useQuery<EnumData>({
    queryKey: ["enums"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/opportunities/enums`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch enums");
      }
      return response.json();
    },
    enabled: !!session?.access_token,
  });

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px'
  });

  const {
    data: opportunities,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "opportunities",
      searchQuery,
      appliedBeneficiaries,
      appliedSDGs,
      appliedFocusAreas,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getOpportunities(session.access_token, {
        page: pageParam,
        limit: LIMIT,
        search: searchQuery,
        beneficiaryTypes: appliedBeneficiaries,
        sdgs: appliedSDGs,
        causeAreas: appliedFocusAreas,
      });
      
      return {
        data: response.data,
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        total: response.pagination.total
      };
    },
    getNextPageParam: (lastPage) => 
      lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
    enabled: !!profile && !!session.access_token,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const opportunitiesList = useMemo(() => {
    return opportunities?.pages.flatMap((page) => page.data) || [];
  }, [opportunities]);

  const activeFiltersCount =
    appliedBeneficiaries.length + appliedSDGs.length + appliedFocusAreas.length;

  const getFormatIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "volunteer":
        return <Users className="h-4 w-4" />;
      case "donation":
        return <Target className="h-4 w-4" />;
      case "grant":
        return <Globe className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getFormatTooltip = (type: string) => {
    switch (type.toLowerCase()) {
      case "volunteer":
        return "Volunteer Opportunity";
      case "donation":
        return "Donation Opportunity";
      case "grant":
        return "Grant Opportunity";
      default:
        return type;
    }
  };

  const handleApplyFilters = () => {
    setIsFilterOpen(false);
  };

  const handleClearFilters = () => {
    setAppliedBeneficiaries([]);
    setAppliedSDGs([]);
    setAppliedFocusAreas([]);
  };

  if (isLoading || isEnumsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search opportunities..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-[300px]"
          />
          <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" onClick={() => setIsFilterOpen(true)}>
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Filter Opportunities</SheetTitle>
                <SheetDescription>
                  Pre-selected filters are based on your organization's profile. You can modify these to find specific opportunities.
                </SheetDescription>
              </SheetHeader>

              <div className="py-4 space-y-4">
                {/* SDGs Filter */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">SDGs</label>
                    {profile?.sdgs?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {profile.sdgs.length} from profile
                      </Badge>
                    )}
                  </div>
                  <Popover open={openSDG} onOpenChange={setOpenSDG}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openSDG}
                        className="w-full justify-between"
                      >
                        {appliedSDGs?.length
                          ? `${appliedSDGs.length} selected`
                          : "Select SDGs..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="Search SDGs..." />
                        <CommandList>
                          <CommandEmpty>No results found.</CommandEmpty>
                          <CommandGroup>
                            {enumData?.data.sdg.map(({ id, value }) => {
                              const isSelected = appliedSDGs?.includes(id);
                              return (
                                <CommandItem
                                  key={id}
                                  onSelect={() => {
                                    const newValues = isSelected
                                      ? appliedSDGs.filter((v) => v !== id)
                                      : [...(appliedSDGs || []), id];
                                    setAppliedSDGs(newValues);
                                  }}
                                >
                                  <div
                                    className={cn(
                                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      isSelected
                                        ? "bg-primary text-primary-foreground"
                                        : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-4 w-4" />
                                  </div>
                                  <span className="text-sm">{`${id} - ${value}`}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {appliedSDGs?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {appliedSDGs.map((sdgId) => {
                        const sdgValue = enumData?.data.sdg.find((s) => s.id === sdgId);
                        return sdgValue ? (
                          <Badge
                            key={sdgValue.id}
                            variant="secondary"
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700"
                          >
                            {`${sdgValue.id} - ${sdgValue.value}`}
                            <button
                              className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  setAppliedSDGs(appliedSDGs.filter((id) => id !== sdgId));
                                }
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setAppliedSDGs(appliedSDGs.filter((id) => id !== sdgId));
                              }}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                {/* Beneficiary Types Filter */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Beneficiary Types</label>
                    {profile?.beneficiary_types?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {profile.beneficiary_types.length} from profile
                      </Badge>
                    )}
                  </div>
                  <Popover open={openBeneficiary} onOpenChange={setOpenBeneficiary}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openBeneficiary}
                        className="w-full justify-between"
                      >
                        {appliedBeneficiaries?.length
                          ? `${appliedBeneficiaries.length} selected`
                          : "Select beneficiary types..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="Search beneficiary types..." />
                        <CommandList>
                          <CommandEmpty>No results found.</CommandEmpty>
                          <CommandGroup>
                            {enumData?.data.opportunityBeneficiaryType.map((type) => {
                              const isSelected = appliedBeneficiaries?.includes(type);
                              return (
                                <CommandItem
                                  key={type}
                                  onSelect={() => {
                                    const newValues = isSelected
                                      ? appliedBeneficiaries.filter((v) => v !== type)
                                      : [...(appliedBeneficiaries || []), type];
                                    setAppliedBeneficiaries(newValues);
                                  }}
                                >
                                  <div
                                    className={cn(
                                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      isSelected
                                        ? "bg-primary text-primary-foreground"
                                        : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-4 w-4" />
                                  </div>
                                  <span className="text-sm">{type}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {appliedBeneficiaries?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {appliedBeneficiaries.map((type) => (
                        <Badge
                          key={type}
                          variant="secondary"
                          className="bg-green-100 hover:bg-green-200 text-green-700"
                        >
                          {type}
                          <button
                            className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                setAppliedBeneficiaries(appliedBeneficiaries.filter((t) => t !== type));
                              }
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setAppliedBeneficiaries(appliedBeneficiaries.filter((t) => t !== type));
                            }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Focus Areas Filter */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Focus Areas</label>
                    {profile?.cause_areas?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {profile.cause_areas.length} from profile
                      </Badge>
                    )}
                  </div>
                  <Popover open={openFocusArea} onOpenChange={setOpenFocusArea}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openFocusArea}
                        className="w-full justify-between"
                      >
                        {appliedFocusAreas?.length
                          ? `${appliedFocusAreas.length} selected`
                          : "Select focus areas..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="Search focus areas..." />
                        <CommandList>
                          <CommandEmpty>No results found.</CommandEmpty>
                          <CommandGroup>
                            {enumData?.data.opportunityType.map((type) => {
                              const isSelected = appliedFocusAreas?.includes(type);
                              return (
                                <CommandItem
                                  key={type}
                                  onSelect={() => {
                                    const newValues = isSelected
                                      ? appliedFocusAreas.filter((v) => v !== type)
                                      : [...(appliedFocusAreas || []), type];
                                    setAppliedFocusAreas(newValues);
                                  }}
                                >
                                  <div
                                    className={cn(
                                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      isSelected
                                        ? "bg-primary text-primary-foreground"
                                        : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-4 w-4" />
                                  </div>
                                  <span className="text-sm">{type}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {appliedFocusAreas?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {appliedFocusAreas.map((type) => (
                        <Badge
                          key={type}
                          variant="secondary"
                          className="bg-purple-100 hover:bg-purple-200 text-purple-700"
                        >
                          {type}
                          <button
                            className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                setAppliedFocusAreas(appliedFocusAreas.filter((t) => t !== type));
                              }
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setAppliedFocusAreas(appliedFocusAreas.filter((t) => t !== type));
                            }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <SheetFooter>
                  <Button variant="outline" onClick={handleClearFilters}>
                    Clear All
                  </Button>
                  <Button onClick={handleApplyFilters}>Apply Filters</Button>
                </SheetFooter>
              </div>

              {/* <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Focus Areas</label>
                    {profile?.cause_areas?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {profile.cause_areas.length} from profile
                      </Badge>
                    )}
                  </div>
                  <ScrollArea className="h-[200px] rounded-md border">
                    <div className="p-4 space-y-2">
                      {enumData?.data.opportunityType.map((type) => (
                        <div key={type} className="flex items-center space-x-2">
                          <Checkbox
                            id={type}
                            checked={appliedFocusAreas.includes(type)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setAppliedFocusAreas([...appliedFocusAreas, type]);
                              } else {
                                setAppliedFocusAreas(
                                  appliedFocusAreas.filter((t) => t !== type)
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor={type}
                            className="text-sm flex items-center gap-1"
                          >
                            {type}
                            {profile?.cause_areas?.includes(type) && (
                              <Badge variant="outline" className="text-xs">
                                Profile
                              </Badge>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div> */}
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {opportunitiesList.map((opportunity) => (
          <Card
            key={opportunity.id}
            className={`cursor-pointer transition-all flex flex-col relative ${
              selectedOpportunity?.id === opportunity.id
                ? "ring-2 ring-primary"
                : "hover:shadow-md"
            }`}
            onClick={() => onSelect(opportunity)}
          >
            <CardContent className="p-3 flex flex-col h-full">
              {/* Header with Name */}
              <h4 className="font-medium text-base mb-1">
                {opportunity.name}
              </h4>
              
              {/* Icon-based fields first */}
              <div className="space-y-0.5 mb-2">
                {/* Volunteer Support */}
                {opportunity.volunteer_support && (
                  <div className="flex items-start text-gray-600 text-xs">
                    <Users className="h-3 w-3 mr-1 flex-shrink-0 mt-0.5" />
                    <span>{opportunity.volunteer_support}</span>
                  </div>
                )}
                
                {/* Impact */}
                {opportunity.impact && (
                  <div className="flex items-start text-gray-600 text-xs">
                    <Globe className="h-3 w-3 mr-1 flex-shrink-0 mt-0.5" />
                    <span>{opportunity.impact}</span>
                  </div>
                )}
                
                {/* Community Need */}
                {opportunity.community_need && (
                  <div className="flex items-start text-xs text-gray-600">
                    <Target className="h-3 w-3 mr-1 flex-shrink-0 mt-0.5" />
                    <span>{opportunity.community_need}</span>
                  </div>
                )}
              </div>
              
              {/* Format and Location */}
              <div className="flex flex-wrap gap-1 mb-2">
                {opportunity.format && (
                  <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded flex items-center">
                    <span className="mr-1">📍</span>
                    {opportunity.format.replace(/_/g, ' ')}
                  </span>
                )}
                {opportunity.location && (
                  <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded flex items-center">
                    <span className="mr-1">🏢</span>
                    {opportunity.location}
                  </span>
                )}
              </div>

              {/* Two column layout for tags and longer fields */}
              <div className="grid grid-cols-2 gap-2 mb-1">
                {/* Left column */}
                <div className="space-y-1.5">
                  {/* Focus Area */}
                  <div>
                    <div className="flex items-center">
                      <span className="text-xs font-medium text-gray-600">Focus Area</span>
                    </div>
                    <div className="mt-0.5">
                      <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                        {opportunity.focus_area.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                  
                  {/* Beneficiary Types */}
                  {opportunity.beneficiary_types && opportunity.beneficiary_types.length > 0 && (
                    <div>
                      <div className="flex items-center">
                        <span className="text-xs font-medium text-gray-600">Beneficiaries</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {opportunity.beneficiary_types.map((type, index) => (
                          <span
                            key={index}
                            className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded"
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Right column */}
                <div className="space-y-1.5">
                  {/* UN Days */}
                  {opportunity.relevant_un_day && opportunity.relevant_un_day.length > 0 && (
                    <div>
                      <div className="flex items-center">
                        <span className="text-xs font-medium text-gray-600">UN Days</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {opportunity.relevant_un_day.map((day, index) => (
                          <span
                            key={index}
                            className="text-xs bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded"
                          >
                            {day.split(' (')[0]}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Outcome and Output - Full width */}
              <div className="space-y-1.5 mt-1">
                {/* Outcome */}
                {opportunity.outcome && (
                  <div>
                    <div className="flex items-center">
                      <span className="text-xs font-medium text-gray-600">Outcome</span>
                    </div>
                    <div className="mt-0.5">
                      <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded inline-block">
                        {opportunity.outcome}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Output */}
                {opportunity.output && (
                  <div>
                    <div className="flex items-center">
                      <span className="text-xs font-medium text-gray-600">Output</span>
                    </div>
                    <div className="mt-0.5">
                      <span className="text-xs bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded inline-block">
                        {opportunity.output}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
            
            {/* Select Button Options - Fixed Position */}
            {selectedOpportunity?.id === opportunity.id && (
              <div className="mt-auto border-t w-full flex">
                <Button
                  variant="ghost"
                  className="w-1/2 rounded-none h-8 hover:bg-primary hover:text-primary-foreground border-r"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNext('template');
                  }}
                >
                  Use this template from scratch
                </Button>
                <Button
                  variant="ghost"
                  className="w-1/2 rounded-none h-8 hover:bg-primary hover:text-primary-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNext('ai');
                  }}
                >
                  AI Assisted creation
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Loading More Indicator */}
      <div 
        ref={loadMoreRef} 
        className="py-4 flex justify-center min-h-[100px] items-center"
      >
        {hasNextPage ? (
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading more...
              </>
            ) : (
              "Load more"
            )}
          </Button>
        ) : (
          <div className="text-center space-y-2">
            <span className="text-sm text-muted-foreground block">No more opportunities</span>
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="link"
                className="text-sm"
                onClick={() => setIsFilterOpen(true)}
              >
                <Filter className="mr-2 h-4 w-4" />
                Change Filters
              </Button>
              {(appliedSDGs.length > 0 || appliedBeneficiaries.length > 0 || appliedFocusAreas.length > 0 || searchQuery) && (
                <Button
                  variant="link"
                  className="text-sm"
                  onClick={() => {
                    setAppliedSDGs([]);
                    setAppliedBeneficiaries([]);
                    setAppliedFocusAreas([]);
                    setSearchInput("");
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={onNext} disabled={!selectedOpportunity}>
          Next
        </Button>
      </div>
    </div>
  );
}